import { getAccessToken } from "@lib/google-auth";

const PPTX_MIME =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const SLIDES_MIME = "application/vnd.google-apps.presentation";
const DRIVE_UPLOAD_URL =
  "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink";
// Either request stalling would otherwise leave the export "generating" forever.
const REQUEST_TIMEOUT_MS = 120_000;

/**
 * Drive's multipart upload is `multipart/related`: JSON metadata part first,
 * media second. FormData would send multipart/form-data, which Drive rejects.
 */
export const buildDriveMultipartBody = (
  metadata: Record<string, string>,
  media: Blob,
  mediaType: string,
  boundary: string,
): Blob =>
  new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
    `--${boundary}\r\nContent-Type: ${mediaType}\r\n\r\n`,
    media,
    `\r\n--${boundary}--`,
  ]);

/**
 * Save a rendered PPTX to the user's Google Drive as a native Google Slides
 * file (Drive converts on upload). Uses the same drive.file token as the
 * Sheets data source; the consent popup must already have been shown from a
 * click handler, this only reads the cached token.
 */
export const uploadToGoogleSlides = async (
  pptxUrl: string,
  title: string,
): Promise<{ id: string; webViewLink: string }> => {
  const token = await getAccessToken();
  const file = await fetch(pptxUrl, {
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!file.ok) throw Error(`Failed to fetch PPTX: ${file.status}`);

  const boundary = `storydoc-${Date.now()}`;
  const res = await fetch(DRIVE_UPLOAD_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": `multipart/related; boundary=${boundary}`,
    },
    body: buildDriveMultipartBody(
      { name: title, mimeType: SLIDES_MIME },
      await file.blob(),
      PPTX_MIME,
      boundary,
    ),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!res.ok) throw Error(`Google Drive upload failed: ${res.status}`);
  return res.json();
};
